<style scoped>
.address-form-group {
    margin-bottom: 0;
}
.address-block-label {
    font-size: 1em;
}
</style>
<template>
    <fieldset class="form-group address-form-group clearfix" :class="[colClass]">
        <template v-if="effectiveLabel == effectiveLabelText">
            <legend class="highlight-on-child-error address-block-label control-label" :class="settings.required && singleLabel ? 'dfRequired' : ''" :style="singleLabel ? 'margin-bottom:10px' : ''">
                {{ effectiveLabelText }}
                <span class="sr-only" v-if="settings.required && singleLabel">Required</span>
                <HelpIcon :text="settings.helpText" v-if="!isViewOnly" />
            </legend>
        </template>
        <ChildrenList :children-ids="children" :view-only="isViewOnly" :force-non-live="settings.verifyAddress != AddressVerificationEnum.Off"></ChildrenList>
        <div class="dfCompositControlChildMargin" v-if="settings.showErrorsLocally">
            <div v-for="child in inputsFields" :key="child.vmId">
                <ErrorList :data="child" :force-non-local="true" :id="child.vmId + '_Errors'" />
            </div>
        </div>
        <Comments :data="data" />
        <AdminSettingsDifferences :data="data" />
        <ErrorList :data="data" />
    </fieldset>
</template>
<script lang="ts">
import { defineComponent, ref, computed, inject, watch } from "vue";
import { combineLatest, from } from "rxjs";
import { useObservable } from "@vueuse/rxjs";
import { map, switchMap } from "rxjs/operators";
import { usePageStore } from "../pageState/store";
import { useMainStore } from "@/mainStore";
import { props, Props, components, setup } from "./BaseDefMixin.3";
import type { BaseControlDefBase, BaseElementDefBase } from "../elements/BaseElementDef";
import type AddressDef from "../elements/AddressDef";
import type { AddressSettings } from "@cs-ts/address-settings";
import type { ObservableReturnType } from "@/lib/observableHelpers";
import AddressConfirmModal from "../dialogs/AddressConfirm.3.vue";
import { showModalInjectKey, type showModal } from "@/AppShell.3.vue.InjectKeys";
import { VerifiedAddress } from "../elements/AddressDef";
import DisplayIfOtherHasValueDef from "../elements/DisplayIfOtherHasValueDef";
import { isNullOrWhitespace } from "@/lib/whitespaceMethods";
import resolvablePromise from "@/lib/resolvablePromise";
import { AddressVerificationEnum } from "@cs-ts/address-verification-enum";
import { asyncComputed } from "@vueuse/core";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";
export default defineComponent({
    props: props,
    components: {
        ...components,
    },
    //@ts-ignore
    setup(props: Props<AddressDef, AddressSettings>) {
        const showModal = inject(showModalInjectKey) as showModal;
        const base = setup<AddressDef, AddressSettings>(props);
        const verificationPromise = ref<ReturnType<typeof resolvablePromise<void>>>(resolvablePromise());
        const pageStore = usePageStore();
        const mainStore = useMainStore();
        const isVerifying = ref(false);

        const addressVerifyEnabled = asyncComputed(() => {
            return mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.EnableAddressVerify.Id);
        });
        const adminReview = computed(() => pageStore.adminReview);
        const singleLabel = computed(() => (base.settings.value as AddressSettings).layout == 1);
        const currentAddressData = computed({
            get() {
                return props.data.currentAddressData;
            },
            set(value) {
                //eslint-disable-next-line vue/no-mutating-props
                props.data.currentAddressData = value;
            },
        });

        const allInputsFields$ = (props.data as BaseElementDefBase)._children$.pipe(
            switchMap((children: ObservableReturnType<BaseElementDefBase["_children$"]>) => combineLatest(children.map(child => [from([[child]]), child._children$]).flat())),
            map(children => children.flat()),
        );
        const allInputsFields = useObservable(allInputsFields$);

        const inputsFields$ = allInputsFields$.pipe(
            switchMap(input => combineLatest((input as BaseControlDefBase[]).map(child => (child.visible$ ? child.visible$().pipe(map(v => (v ? child : null))) : from([child]))))),
            map(children => children.filter(i => i != null) as BaseElementDefBase[]),
        );
        const inputsFields = useObservable(inputsFields$);

        if (base.settings.value.verifyAddress != AddressVerificationEnum.Off) {
            const inputsFieldsEdited$ = inputsFields$.pipe(
                map(input => input.filter(i => !(i instanceof DisplayIfOtherHasValueDef))),
                switchMap(input => combineLatest((input as BaseControlDefBase[]).map(i => i.edited$()))),
            );
            const inputsFieldsEdited = useObservable(inputsFieldsEdited$);

            const verifyAddress = async () => {
                const deletePromise = () => {
                    verificationPromise.value.resolve();
                    pageStore.pendingAddressVerifications.delete(verificationPromise.value!);
                };
                const { Address1: address1, Address2: address2, City: city, State: state, Zip: zip, Country: country } = await getAddressData();
                const userEnteredAddress = { Address1: address1, Address2: address2, City: city, State: state, Zip: zip, Country: country };
                if (isNullOrWhitespace(address1) || isNullOrWhitespace(city) || isNullOrWhitespace(state) || isNullOrWhitespace(zip) || (base.settings.value.allowInternational ? isNullOrWhitespace(country) : false) || JSON.stringify(userEnteredAddress) == currentAddressData.value || isVerifying.value) {
                    return;
                }
                isVerifying.value = true;
                (async () => {
                    if (!pageStore.pendingAddressVerifications.has(verificationPromise.value)) {
                        pageStore.pendingAddressVerifications.add(verificationPromise.value);
                    }
                    const otherPendingAddressVerifications = Array.from(pageStore.pendingAddressVerifications).filter(p => p !== verificationPromise.value);
                    if (otherPendingAddressVerifications.length > 0) {
                        await Promise.all(otherPendingAddressVerifications);
                    }

                    let suggestionPopulated = false;
                    const populateSuggestedAddress = (suggested: VerifiedAddress) => {
                        const address1Control = getControl("Address1", true);
                        const address2Control = getControl("Address2", true);
                        const cityControl = getControl("City", true);
                        const stateControl = getControl("State", true);
                        const zipControl = getControl("Zip", true);
                        const provinceControl = getControl("Province", true);
                        const regionControl = getControl("Region", true);
                        const postalCodeControl = getControl("PostalCode", true);
                        const countryControl = getControl("Country", true);

                        const address1 = suggested.Address1 ?? "";
                        const address2 = suggested.Address2 ?? "";
                        const city = suggested.City ?? "";
                        const state = suggested.State ?? "";
                        const zip = suggested.ZipCode ?? "";
                        const country = suggested.Country ?? "";

                        address1Control?.setValue(address1);
                        address2Control?.setValue(address2);
                        cityControl?.setValue(city);
                        country == "" || country == "US" ? stateControl?.setValue(state) : country == "CA" ? provinceControl?.setValue(state) : regionControl?.setValue(state);
                        country == "" || country == "US" ? zipControl?.setValue(zip) : postalCodeControl?.setValue(zip);
                        countryControl?.setValue(country);

                        suggestionPopulated = true;
                        currentAddressData.value = JSON.stringify({ Address1: address1, Address2: address2, City: city, State: state, Zip: zip, Country: country });
                    };

                    const { Success: success, AddressChanged: addressChanged, Result: suggestedAddress } = await props.data.verifyAddress(JSON.stringify(userEnteredAddress));
                    if (success && addressChanged) {
                        showModal(
                            AddressConfirmModal,
                            {
                                data: props.data,
                                userEnteredAddress,
                                suggestedAddress,
                                populateSuggestedAddress,
                                triggerElement: document.activeElement,
                            },
                            {
                                "before-close"() {
                                    if (base.settings.value.verifyAddress == AddressVerificationEnum.Verify && !suggestionPopulated) {
                                        currentAddressData.value = JSON.stringify(userEnteredAddress);
                                    } else if (base.settings.value.verifyAddress == AddressVerificationEnum.VerifyReFormat) {
                                        populateSuggestedAddress(suggestedAddress);
                                    }
                                    deletePromise();
                                },
                            },
                            {
                                ...(base.settings.value.verifyAddress == AddressVerificationEnum.Verify ? { "click-to-close": false } : {}),
                            },
                        );
                    } else {
                        deletePromise();
                    }
                    await verificationPromise.value;
                })().finally(() => {
                    isVerifying.value = false;
                    deletePromise();
                    verificationPromise.value = resolvablePromise();
                });
            };
            const getAddressData = async () => {
                const getValue = (name: string) => getControl(name)?.effectiveValueList();
                const address1 = (await getValue("Address1"))?.[0] ?? "";
                const address2 = (await getValue("Address2"))?.[0] ?? "";
                const city = (await getValue("City"))?.[0] ?? "";
                const state = (await (getValue("State") ?? getValue("Province") ?? getValue("Region")))?.[0] ?? "";
                const zip = (await (getValue("Zip") ?? getValue("PostalCode")))?.[0] ?? "";
                const country = (await getValue("Country"))?.[0] ?? "";

                return { Address1: address1, Address2: address2, City: city, State: state, Zip: zip, Country: country };
            };
            const getControl = (name: string, includeAll: boolean = false) => {
                return ((includeAll ? allInputsFields.value : inputsFields.value) ?? [])?.find(i => i.id?.includes(name)) as BaseControlDefBase | null;
            };

            watch(inputsFieldsEdited, () => {
                if (addressVerifyEnabled.value && !isVerifying.value) {
                    verifyAddress();
                }
            });
        }

        return {
            AddressVerificationEnum,
            singleLabel,
            inputsFields,
            effectiveLabel: useObservable(props.data.effectiveLabel$()),
            effectiveLabelText: useObservable(props.data.effectiveLabelText$()),
            ...base.expose,
            adminReview,
        };
    },
});
</script>
